/**
 * Shared utilities for "Create an Idea from agent chat" — parsing, option
 * building, and message detection. Consumed by both the Explore and idea-page
 * agent chat flows.
 */

export const CREATE_IDEA_MSG_PREFIX = 'CREATE_IDEA:';

// ---------------------------------------------------------------------------
// Option model
// ---------------------------------------------------------------------------

export interface CreateIdeaOption {
  /** Label shown in the inline-prompt list */
  label: string;
  /**
   * Payload sent after the prefix:
   *   "query:<text>"  → insights from a specific user query
   */
  payload: string;
  /** _id of the source message this option was built from (used to key the saved one-pager). */
  messageId?: string;
}

// ---------------------------------------------------------------------------
// Option builder
// ---------------------------------------------------------------------------

export function getPersistedMessageId(m: any): string | undefined {
  const raw = m?._id ?? m?.id ?? m?.messageId;
  if (raw == null) return undefined;
  const s = String(raw);
  if (s.startsWith('temp-')) return undefined;
  return s;
}

/**
 * True when message content is a hidden create-idea prompt — used to filter
 * these system messages from the visible thread.
 * Matches both the old CREATE_IDEA: prefix and the current "create a idea based on" format.
 */
export function isHiddenCreateIdeaUserMessage(content: string | null | undefined): boolean {
  if (!content) return false;
  const trimmed = content.trimStart();
  return (
    trimmed.startsWith(CREATE_IDEA_MSG_PREFIX) ||
    trimmed.toLowerCase().startsWith('create a idea based on "')
  );
}

/**
 * True when message content is an IDEA_CREATED notification.
 * These appear in the chat thread but must NOT appear in the options list.
 */
export function isIdeaCreatedMessage(content: string | null | undefined): boolean {
  if (!content) return false;
  return content.trimStart().startsWith('IDEA_CREATED');
}

/**
 * Build the list of selectable create-idea options from the current message history.
 * Returns one entry per visible user query.
 */
export function buildCreateIdeaOptions(messages: any[]): CreateIdeaOption[] {
  const options: CreateIdeaOption[] = [];

  messages
    .filter(
      (m) =>
        m.messageType === 'user' &&
        !isHiddenCreateIdeaUserMessage(m.content) &&
        !isIdeaCreatedMessage(m.content)
    )
    .forEach((m) => {
      const raw: string = (m.content ?? '').trim();
      if (!raw) return;
      const preview = raw.length > 110 ? raw.slice(0, 107) + '…' : raw;
      const messageId = getPersistedMessageId(m);
      options.push({
        label: `"${preview}"`,
        payload: raw,
        ...(messageId != null ? { messageId } : {}),
      });
    });

  return options;
}

// ---------------------------------------------------------------------------
// Hidden message construction
// ---------------------------------------------------------------------------

/**
 * Builds the hidden message sent to the ML API.
 * Format: create a idea based on "<selected query text>"
 */
export function buildCreateIdeaHiddenMessage(payload: string): string {
  return `create a idea based on "${payload}"`;
}

// ---------------------------------------------------------------------------
// IDEA_CREATED message parsing
// ---------------------------------------------------------------------------

export interface IdeaCreatedInfo {
  ideaId: string;
  title: string;
  description: string;
}

/**
 * Parse an IDEA_CREATED message into its parts.
 * Format:
 *   IDEA_CREATED
 *   Idea ID: <id>
 *   Title: <title>
 *   Description: <description>
 */
export function parseIdeaCreatedMessage(content: string): IdeaCreatedInfo {
  const lines = content.split('\n');
  const ideaId = (
    lines.find((l) => /^idea id:/i.test(l)) ??
    lines.find((l) => /^idea_id:/i.test(l)) ??
    ''
  )
    .replace(/^idea[_ ]id:/i, '')
    .trim();
  const title = (lines.find((l) => /^title:/i.test(l)) ?? '')
    .replace(/^title:/i, '')
    .trim();
  const description = (lines.find((l) => /^description:/i.test(l)) ?? '')
    .replace(/^description:/i, '')
    .trim();
  return { ideaId, title, description };
}

// ---------------------------------------------------------------------------
// AI response parsing
// ---------------------------------------------------------------------------

export interface CreateIdeaContent {
  title: string;
  description: string;
}

/**
 * Parse the AI response into { title, description }.
 * Tries JSON first; falls back to heading extraction.
 */
export function parseCreateIdeaResponse(raw: string): CreateIdeaContent {
  const cleaned = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/, '').trim();

  // 1. Direct JSON parse
  try {
    const parsed = JSON.parse(cleaned);
    if (parsed.title && parsed.description) {
      return {
        title: String(parsed.title).trim(),
        description: String(parsed.description).trim(),
      };
    }
  } catch {
    /* fall through */
  }

  // 2. JSON embedded in prose
  const jsonMatch = cleaned.match(/\{[\s\S]*?"title"[\s\S]*?"description"[\s\S]*?\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      if (parsed.title && parsed.description) {
        return {
          title: String(parsed.title).trim(),
          description: String(parsed.description).trim(),
        };
      }
    } catch {
      /* fall through */
    }
  }

  // 3. Markdown fallback — extract ## Title + rest as description
  const titleMatch = cleaned.match(/^##\s+(.+)$/m);
  const title = titleMatch
    ? titleMatch[1].trim()
    : (cleaned.split('\n')[0] ?? '').replace(/^#+\s*/, '').trim();

  const afterTitle = titleMatch
    ? cleaned.slice((titleMatch.index ?? 0) + titleMatch[0].length).trim()
    : cleaned.split('\n').slice(1).join('\n').trim();

  return { title, description: afterTitle.replace(/^##\s+Description\s*/i, '').trim() };
}
